import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Bell, User, X, Trash2, Settings, LogOut, ChevronDown, Moon, Sun } from 'lucide-react';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;
const ADMIN_READ_STORAGE_KEY = 'fitpulse_admin_notifications_read';
const POLL_INTERVAL_MS = 15000;

function formatRelativeTime(isoString) {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return 'Baru saja';
  const diffSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diffSeconds < 60) return 'Baru saja';
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)} menit lalu`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)} jam lalu`;
  return `${Math.floor(diffSeconds / 86400)} hari lalu`;
}

function readStoredIds() {
  try {
    const raw = localStorage.getItem(ADMIN_READ_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

export function Header({ title = 'Admin Panel', onSearch }) {
  const { user, logout } = useAuth();
  const { isLight, toggleTheme } = useTheme();
  const role = String(user?.role || '').trim().toLowerCase();
  const isInvestor = role === 'investor';
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [readIds, setReadIds] = useState(() => readStoredIds());

  useEffect(() => {
    let cancelled = false;

    const fetchNotifications = async () => {
      try {
        const { data } = await axios.get(`${API}/notifications?limit=30`, { withCredentials: true });
        if (!cancelled) {
          setNotifications(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch notifications:', error);
        }
      }
    };

    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(ADMIN_READ_STORAGE_KEY, JSON.stringify(readIds.slice(0, 300)));
  }, [readIds]);

  const readIdSet = useMemo(() => new Set(readIds), [readIds]);
  const enrichedNotifications = useMemo(
    () =>
      notifications.map((item) => ({
        ...item,
        read: readIdSet.has(item.id),
        time: formatRelativeTime(item.created_at),
      })),
    [notifications, readIdSet]
  );

  const unreadCount = enrichedNotifications.filter((n) => !n.read).length;

  const markAsRead = (id) => {
    setReadIds((prev) => (prev.includes(id) ? prev : [id, ...prev]));
  };

  const markAllAsRead = () => {
    setReadIds((prev) => {
      const union = new Set(prev);
      for (const notification of notifications) {
        union.add(notification.id);
      }
      return Array.from(union);
    });
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`, { withCredentials: true });
      setNotifications((prev) => prev.filter((item) => item.id !== id));
      setReadIds((prev) => prev.filter((readId) => readId !== id));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const deleteAllNotifications = async () => {
    if (!notifications.length) return;
    if (!window.confirm('Hapus semua notifikasi?')) return;
    try {
      await axios.delete(`${API}/notifications`, { withCredentials: true });
      setNotifications([]);
      setReadIds([]);
    } catch (error) {
      console.error('Failed to delete all notifications:', error);
    }
  };

  return (
    <header className="h-16 border-b border-white/10 flex items-center justify-between px-8 bg-[#09090B]/80 backdrop-blur-xl sticky top-0 z-50">
      {/* Title */}
      <h2 className="text-lg font-semibold text-white tracking-tight" data-testid="header-title">
        {title}
      </h2>

      {/* Right Section */}
      <div className="flex items-center gap-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" strokeWidth={1.5} />
          <Input
            type="text"
            placeholder="Search members..."
            onChange={(e) => onSearch?.(e.target.value)}
            className="w-64 pl-10 bg-zinc-900/50 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-sm h-9 text-sm"
            data-testid="header-search-input"
          />
        </div>

        <button
          type="button"
          onClick={toggleTheme}
          className="inline-flex h-9 items-center gap-2 rounded-sm border border-white/10 bg-zinc-900/45 px-3 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-300 transition-colors hover:border-[#D4AF37]/35 hover:text-[#D4AF37]"
          data-testid="theme-toggle-btn"
          aria-label={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
        >
          {isLight ? (
            <Moon className="h-4 w-4" strokeWidth={1.7} />
          ) : (
            <Sun className="h-4 w-4" strokeWidth={1.7} />
          )}
          {isLight ? 'Dark' : 'Light'}
        </button>

        {/* Notification */}
        <div className="relative">
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 rounded-sm hover:bg-zinc-900/50 transition-colors cursor-pointer"
            data-testid="notification-btn"
          >
            <Bell className="w-5 h-5 text-zinc-400 hover:text-white transition-colors" strokeWidth={1.5} />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-[#D4AF37] rounded-full" />
            )}
          </button>

          {/* Notification Dropdown */}
          {showNotifications && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowNotifications(false)}
              />
              
              {/* Dropdown */}
              <div className="absolute right-0 top-12 w-80 bg-zinc-900 border border-white/10 rounded-sm shadow-xl z-50 overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  <h3 className="text-sm font-semibold text-white">Notifikasi</h3>
                  <button 
                    onClick={() => setShowNotifications(false)}
                    className="p-1 hover:bg-zinc-800 rounded-sm transition-colors"
                    data-testid="close-notifications-btn"
                  >
                    <X className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
                  </button>
                </div>
                
                <div className="max-h-80 overflow-y-auto">
                  {enrichedNotifications.length > 0 ? (
                    enrichedNotifications.map((notification) => (
                      <div 
                        key={notification.id}
                        onClick={() => markAsRead(notification.id)}
                        className={`p-4 border-b border-white/5 hover:bg-zinc-800/50 transition-colors cursor-pointer ${
                          !notification.read ? 'bg-[#D4AF37]/5' : ''
                        }`}
                        data-testid={`notification-item-${notification.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            className="p-1 rounded-sm text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            data-testid={`delete-notification-btn-${notification.id}`}
                            aria-label="Delete notification"
                          >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </button>
                          {!notification.read && (
                            <span className="w-2 h-2 bg-[#D4AF37] rounded-full mt-1.5 flex-shrink-0" />
                          )}
                          <div className={`flex-1 ${!notification.read ? '' : 'pl-5'}`}>
                            <p className="text-sm text-white">{notification.message}</p>
                            <p className="text-xs text-zinc-500 mt-1">{notification.time}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center">
                      <Bell className="w-8 h-8 text-zinc-600 mx-auto mb-2" strokeWidth={1.5} />
                      <p className="text-sm text-zinc-500">Tidak ada notifikasi</p>
                    </div>
                  )}
                </div>
                
                <div className="p-3 border-t border-white/10">
                  <div className="grid grid-cols-2 gap-2">
                    <button 
                      onClick={markAllAsRead}
                      className="text-center text-xs text-[#D4AF37] hover:text-[#C59A27] transition-colors py-2"
                      data-testid="mark-all-read-btn"
                    >
                      Tandai dibaca
                    </button>
                    <button 
                      onClick={deleteAllNotifications}
                      className="text-center text-xs text-red-400 hover:text-red-300 transition-colors py-2"
                      data-testid="delete-all-notifications-btn"
                    >
                      Hapus semua
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Profile */}
        <div className="relative pl-4 border-l border-white/10">
          <button
            onClick={() => setShowProfileMenu((prev) => !prev)}
            className="flex items-center gap-3 rounded-sm px-2 py-1.5 transition-colors hover:bg-zinc-900/60"
            data-testid="profile-menu-btn"
          >
            <div className="text-right">
              <p className="text-sm font-medium text-white" data-testid="user-name">{user?.name || 'Admin'}</p>
              <p className="text-xs text-zinc-500">{user?.role || 'Administrator'}</p>
            </div>
            <div className="w-9 h-9 bg-zinc-800 rounded-sm flex items-center justify-center border border-white/10">
              <User className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
            </div>
            <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} strokeWidth={1.5} />
          </button>

          {showProfileMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowProfileMenu(false)}
              />
              <div className="absolute right-0 top-12 z-50 w-60 overflow-hidden rounded-sm border border-white/10 bg-zinc-900 shadow-xl">
                <div className="border-b border-white/10 p-4">
                  <p className="truncate text-sm font-semibold text-white">{user?.name || 'Admin'}</p>
                  <p className="mt-1 truncate text-xs text-zinc-500">{user?.email || 'admin@fitpulse.local'}</p>
                </div>
                <div className="p-2">
                  {isInvestor ? null : (
                    <Link
                      to="/settings"
                      onClick={() => setShowProfileMenu(false)}
                      className="flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-white"
                      data-testid="profile-settings-link"
                    >
                      <Settings className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.5} />
                      Profil & Settings
                    </Link>
                  )}
                  <button
                    onClick={logout}
                    className="flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-sm text-zinc-300 transition-colors hover:bg-red-500/10 hover:text-red-300"
                    data-testid="profile-logout-btn"
                  >
                    <LogOut className="h-4 w-4 text-red-400" strokeWidth={1.5} />
                    Logout
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
